import { useState } from 'react'

import { fromCents, toPositive } from '@ankor-io/common/domain/pay/currency'
import { formatCurrency } from '@ankor-io/common/util/currencies'

import { useCardSpendingStats } from '@/hooks/useCardSpendingStats'
import { Heading } from '@/ui-components/Heading'
import Loading from '@/ui-components/Loading'
import { Select } from '@/ui-components/Select'
import { Text } from '@/ui-components/Text'

import { CardSpendChart } from './CardSpendChart'
import { CardTransactions } from './CardTransactions'

type CardSpendProps = {
  cardId: string
}

export const CardSpend = ({ cardId }: CardSpendProps) => {
  const { stats, isLoading } = useCardSpendingStats(cardId)
  const [selectedCurrency, setSelectedCurrency] = useState<string>('')

  // Get the primary currency (for total spend display)
  const getPrimaryCurrency = () => {
    const availableCurrencies = stats?.last7Days?.currencies || ['USD']
    return availableCurrencies[0] || 'USD'
  }

  // Calculate total spend for the past 7 days (always in primary currency)
  const calculateTotalSpend = () => {
    if (!stats?.last7Days?.total) return 0

    const primaryCurrency = getPrimaryCurrency()
    const totalCents = stats.last7Days.total[primaryCurrency]?.totalSpentCents || 0
    return toPositive(fromCents(totalCents))
  }

  if (isLoading) {
    return (
      <div className="w-full bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <Heading level={3}>Card spend</Heading>
        </div>
        <div className="p-4">
          <Loading />
        </div>
      </div>
    )
  }

  const totalSpend = calculateTotalSpend()
  const primaryCurrency = getPrimaryCurrency()
  const availableCurrencies = stats?.last7Days?.currencies || ['USD']

  return (
    <div className="w-full bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
      <div className="p-4 border-b border-gray-200 dark:border-gray-700">
        <Heading level={3}>Card spend</Heading>
      </div>
      <div className="p-4 space-y-6">
        {/* Summary Section */}
        <div className="flex items-center justify-between">
          <div className="flex flex-col gap-1">
            <Text className="text-sm">Total spend past 7 days</Text>
            <div className="text-2xl font-bold text-gray-900 dark:text-white">
              {formatCurrency(totalSpend, primaryCurrency)}
            </div>
          </div>
          {availableCurrencies.length > 0 && (
            <div className="flex items-center gap-2">
              <Text className="text-sm text-gray-600 dark:text-gray-400">Currency:</Text>
              <Select
                value={selectedCurrency || availableCurrencies[0]}
                onChange={(e) => setSelectedCurrency(e.target.value)}
                className="w-20"
              >
                {availableCurrencies.map((currency) => (
                  <option
                    key={currency}
                    value={currency}
                  >
                    {currency}
                  </option>
                ))}
              </Select>
            </div>
          )}
        </div>

        {/* Chart */}
        <CardSpendChart
          stats={stats}
          selectedCurrency={selectedCurrency}
        />

        {/* CardTransactions Component */}
        <CardTransactions cardId={cardId} />
      </div>
    </div>
  )
}

export default CardSpend
